// Marketing display stats only. Do NOT import enabledBrands / data/index here
// (avoids shipping large brand catalogs into client bundles via shared imports).
// Bump these scalars manually when inventory changes meaningfully.
// The committed default share image shows brandCount: after a bump, run
// `pnpm dev` and `pnpm run render-share-default` (a test fails until you do).
const { defaultLocale } = require('../src/i18n.json');

const siteStats = {
  // "More than": shown with a trailing plus, e.g. "4,700+".
  brandCount: 4700,
  countryCount: 100,
  // Exact.
  networkCount: 17,
};

const formatCount = (count, locale) => new Intl.NumberFormat(locale).format(count);

/**
 * Display labels in the number format of `locale`: "4,700+" (en), "4.700+"
 * (de), "4700+" (pl). A comma is the decimal separator in most of our locales,
 * so the English label must never reach another language.
 *
 * These are also the ICU / next-intl vars behind the `{brandCount}`,
 * `{countryCount}` and `{networkCount}` placeholders.
 *
 * @param {string} [locale]  Defaults to the default locale, for text that is
 *   only ever written in English (JSON-LD descriptions, llms.txt).
 * @returns {{ brandCount: string, countryCount: string, networkCount: string }}
 */
siteStats.labels = (locale = defaultLocale) => ({
  brandCount: `${formatCount(siteStats.brandCount, locale)}+`,
  countryCount: `${formatCount(siteStats.countryCount, locale)}+`,
  networkCount: formatCount(siteStats.networkCount, locale),
});

/**
 * Replace {brandCount}, {countryCount}, {networkCount} in a locale's message
 * tree. Call when loading messages so every t() call gets concrete marketing
 * numbers without importing brand catalogs.
 *
 * @param {object|string} messages
 * @param {string} locale  The locale of `messages`: it sets the number format.
 */
function applyMarketingStatsToMessages(messages, locale) {
  if (!locale) {
    throw new Error('applyMarketingStatsToMessages: pass the locale of the messages');
  }

  const { brandCount, countryCount, networkCount } = siteStats.labels(locale);

  const replaceInString = (str) =>
    str
      .replaceAll('{brandCount}', brandCount)
      .replaceAll('{countryCount}', countryCount)
      .replaceAll('{networkCount}', networkCount);

  const walk = (value) => {
    if (typeof value === 'string') return replaceInString(value);
    if (Array.isArray(value)) return value.map(walk);
    if (value && typeof value === 'object') {
      const out = {};
      for (const [key, child] of Object.entries(value)) {
        out[key] = walk(child);
      }
      return out;
    }
    return value;
  };

  return walk(messages);
}

siteStats.applyMarketingStatsToMessages = applyMarketingStatsToMessages;

/** Substitute marketing placeholders in a single string (FAQs, SEO built in getStaticProps). */
siteStats.interpolate = (str, locale) => {
  if (typeof str !== 'string') return str;
  return siteStats.applyMarketingStatsToMessages(str, locale);
};

module.exports = siteStats;
